import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { copyToClipboard } from '@/utils/vuex';
import get from 'lodash/get';
export default {
  name: 'CoreTwoFactorAuthModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    cancelBtnIcon: {
      type: String,
      default: 'fa-times'
    },
    cancelBtnText: {
      type: String,
      default: ''
    },
    isLoginFlow: {
      // true, shows paragraph about MFA being mandatory when used in login flow
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inputVerifyToken: '',
      qrImage: null,
      setupStep: 1,
      recoveryCodes: [],
      errorVerify: false,
      verifyErrorMessageKey: 'Common.multiFactorAuth.verifyToken_error',
      errorQrCode: false,
      isLoadingQr: false,
      isVerifying: false,
      requiresCompletion: false,
      isCompleting: false,
      errorComplete: false,
      completeErrorMessageKey: 'Common.multiFactorAuth.verifyToken_genericError'
    };
  },
  computed: {
    ...mapState('app', ['color'])
  },
  watch: {
    isOpen(value) {
      if (value) {
        this.resetModal();
        this.getQRCode();
      }
    }
  },
  methods: {
    get,
    copyCodesToClipboard() {
      try {
        copyToClipboard(this.recoveryCodes.map(code => code.trim()).join('\n'));
      } catch (error) {
        console.error('Error copying codes to clipboard', error);
      }
    },
    emitCloseEvent() {
      if (this.isLoginFlow) {
        this.$emit('canceled');
        return;
      }
      const event = this.setupStep < 3 ? 'canceled' : 'completed';
      this.$emit(event);
    },
    resetModal() {
      this.setupStep = 1;
      this.qrImage = null;
      this.recoveryCodes = [];
      this.errorQrCode = false;
      this.errorVerify = false;
      this.verifyErrorMessageKey = 'Common.multiFactorAuth.verifyToken_error';
      this.isLoadingQr = false;
      this.isVerifying = false;
      this.requiresCompletion = false;
      this.isCompleting = false;
      this.errorComplete = false;
      this.completeErrorMessageKey = 'Common.multiFactorAuth.verifyToken_genericError';
      this.inputVerifyToken = '';
    },
    getQRCode() {
      const userid = get(this, '$store.state.authentication.user.id') || null;
      this.isLoadingQr = true;
      this.errorQrCode = false;
      this.qrImage = null;
      ApiService.enableTwoFactorAuth(userid).then(res => {
        this.qrImage = res.qr;
      }).catch(() => {
        this.errorQrCode = true;
      }).finally(() => {
        this.isLoadingQr = false;
      });
    },
    handleQrCodeScanned() {
      if (!this.qrImage || this.errorQrCode || this.isLoadingQr) {
        return;
      }
      this.setupStep = 2;
    },
    handleCodeChanged(value) {
      const sanitizedToken = `${value || ''}`.replace(/\D/g, '').slice(0, 6);
      if (sanitizedToken !== this.inputVerifyToken) {
        this.inputVerifyToken = sanitizedToken;
      }
      if (this.errorVerify) {
        this.errorVerify = false;
      }
      this.verifyErrorMessageKey = 'Common.multiFactorAuth.verifyToken_error';
    },
    handleCodeEntered() {
      if (this.inputVerifyToken.length !== 6 || this.isVerifying) {
        return;
      }
      const userid = get(this, '$store.state.authentication.user.id') || null;
      this.isVerifying = true;
      this.errorVerify = false;
      this.verifyErrorMessageKey = 'Common.multiFactorAuth.verifyToken_error';
      ApiService.enableTwoFactorAuth(userid, this.inputVerifyToken, {
        enforced: this.isLoginFlow === true
      }).then(res => {
        this.setupStep = 3;
        this.recoveryCodes = Array.isArray(res.recoveryCodes) ? res.recoveryCodes : [];
        this.requiresCompletion = this.isLoginFlow && (res === null || res === void 0 ? void 0 : res.setupState) === 'verified';
      }).catch(error => {
        var _error$response, _error$response2;
        const apiErrorMessage = (error === null || error === void 0 || (_error$response = error.response) === null || _error$response === void 0 || (_error$response = _error$response.data) === null || _error$response === void 0 ? void 0 : _error$response.message) || (error === null || error === void 0 || (_error$response2 = error.response) === null || _error$response2 === void 0 || (_error$response2 = _error$response2.data) === null || _error$response2 === void 0 || (_error$response2 = _error$response2.data) === null || _error$response2 === void 0 ? void 0 : _error$response2.message) || (error === null || error === void 0 ? void 0 : error.message) || '';
        const isInvalidTokenError = apiErrorMessage === 'INVALID_TOKEN';
        this.errorVerify = true;
        this.verifyErrorMessageKey = isInvalidTokenError ? 'Common.multiFactorAuth.verifyToken_error' : 'Common.multiFactorAuth.verifyToken_genericError';
      }).finally(() => {
        this.isVerifying = false;
      });
    },
    handleFinishClicked() {
      if (this.isCompleting) {
        return;
      }
      if (!this.requiresCompletion) {
        this.$emit('completed');
        return;
      }
      const userid = get(this, '$store.state.authentication.user.id') || null;
      this.isCompleting = true;
      this.errorComplete = false;
      this.completeErrorMessageKey = 'Common.multiFactorAuth.verifyToken_genericError';
      ApiService.enableTwoFactorAuth(userid, null, {
        complete: true,
        enforced: true
      }).then(res => {
        if ((res === null || res === void 0 ? void 0 : res.completed) !== true) {
          throw new Error('MFA_SETUP_NOT_COMPLETED');
        }
        this.requiresCompletion = false;
        this.$emit('completed');
      }).catch(() => {
        this.errorComplete = true;
        this.completeErrorMessageKey = 'Common.multiFactorAuth.verifyToken_genericError';
      }).finally(() => {
        this.isCompleting = false;
      });
    }
  }
};